/**
 * schema.org JSON-LD builders. One module so every page references
 * the same shapes — easier to audit in Rich Results Test if something
 * regresses.
 *
 * Don't manually template-string these. JsonLd.tsx renders the result
 * via JSON.stringify, which handles all escaping correctly.
 */

import { AGENT, SITE_URL } from "./seo";
import { formatProjectName, removeHTMLTags } from "./utils";
import { youtubeEmbedUrl, youtubeId, youtubeThumb, youtubeWatchUrl } from "./v2/video";

const SITE_NAME = "AI Property";
const SITE_LOGO = `${SITE_URL}/AI-property-Logo-Vertical.png`;

const SOCIAL_LINKS = [
  // Pulled from NewFooter. Keep in sync if those change.
  "https://www.facebook.com/aipropertysg",
  "https://www.instagram.com/aipropertysg",
  "https://www.youtube.com/@aipropertysg",
  "https://www.linkedin.com/company/aipropertysg",
];

/** Organization schema for the root layout. */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    sameAs: SOCIAL_LINKS,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Sales",
      telephone: "+65-8877-2688",
      areaServed: "SG",
      availableLanguage: ["English"],
    },
  };
}

/**
 * WebSite schema + SearchAction. The SearchAction enables Google's
 * sitelinks search box on the brand SERP — when someone searches
 * "AI Property" on Google, the result gets an inline search box that
 * forwards queries to /buy?keyword=...
 */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/buy?keyword={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * RealEstateAgent schema for /about and /contact. More specific than
 * a generic LocalBusiness — tells Google + AI engines this is a
 * licensed property agent operating in Singapore, which is the most
 * granular `@type` that matches what the site actually does. Surfaces
 * in:
 *   - Google's Business Knowledge Panel (when paired with GMB)
 *   - Local pack results for "property agent Singapore"
 *   - AI Overviews / ChatGPT citations as a verified Singapore agent
 *
 * Pairs with the visible CEA registration info already shown in
 * NewFooter.tsx — schema reinforces what users can already see, which
 * is what Google rewards (no schema-stuffing risk).
 */
export function realEstateAgentSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: SITE_NAME,
    legalName: "Elementay Pte Ltd",
    description:
      "Singapore's AI-driven new launch property platform, built and run by Oblique Branding Sdn. Bhd., with marketing by Elementay Pte Ltd, in association with Huttons Asia Pte Ltd. Licensed CEA representation across condos, ECs, and freehold developments.",
    url: SITE_URL,
    logo: SITE_LOGO,
    image: SITE_LOGO,
    telephone: "+65-8877-2688",
    sameAs: SOCIAL_LINKS,
    areaServed: {
      "@type": "Country",
      name: "Singapore",
    },
    knowsAbout: [
      "Singapore new launch property",
      "Singapore condominiums",
      "Singapore executive condominiums",
      "Singapore freehold property",
      "Singapore property investment",
      "Singapore property districts",
    ],
    parentOrganization: {
      "@type": "RealEstateAgent",
      name: "Huttons Asia Pte Ltd",
      // CEA Estate Agent Licence — publicly verifiable on the CEA
      // public register at cea.gov.sg.
      identifier: "L3008899K",
    },
    employee: {
      "@type": "Person",
      name: AGENT.name,
      jobTitle: AGENT.jobTitle,
      // CEA Salesperson Registration — publicly verifiable on the
      // CEA public register at cea.gov.sg.
      identifier: AGENT.ceaRegNo,
      worksFor: {
        "@type": "RealEstateAgent",
        name: AGENT.estateAgent,
        identifier: AGENT.ceaLicenceNo,
      },
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Sales",
      telephone: "+65-8877-2688",
      areaServed: "SG",
      availableLanguage: ["English"],
    },
  };
}

/**
 * BreadcrumbList schema. Each item's `position` is 1-indexed.
 * Items without a URL are still valid — Google's spec only requires
 * `position` + `name`. Pair with the visible <Breadcrumbs> component.
 */
export function breadcrumbListSchema(
  items: Array<{ name: string; url?: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}

interface ArticleForSchema {
  title: string;
  excerpt: string;
  body?: string;
  coverImage?: string;
  author?: string;
  publishedAt?: string | Date;
  updatedAt?: string | Date;
  topic?: string;
}

/**
 * BlogPosting schema for the single-article page. Powers Google's
 * "Article" rich result and AI Overview attribution. Pair with
 * BreadcrumbList on the same page.
 */
export function articleSchema(
  article: ArticleForSchema,
  canonicalUrl: string,
  coverImageUrl?: string,
) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    url: canonicalUrl,
    // E-E-A-T: author as a Person with named credentials beats a
    // generic Organization byline. Google + AI engines weight named,
    // credentialed authors heavily for YMYL content (property is YMYL).
    // Older article docs were stored with author="AI Property" (the
    // org name, not a person); treat any non-Person-shaped string as
    // a null and fall through to the canonical agent.
    author: {
      "@type": "Person",
      name:
        article.author &&
        !/^(ai property|aiproperty|admin|team)$/i.test(article.author.trim())
          ? article.author
          : AGENT.name,
      jobTitle: AGENT.jobTitle,
      identifier: AGENT.ceaRegNo,
      url: `${SITE_URL}/about`,
      worksFor: {
        "@type": "RealEstateAgent",
        name: AGENT.estateAgent,
        identifier: AGENT.ceaLicenceNo,
      },
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: SITE_LOGO },
    },
  };
  if (coverImageUrl) schema.image = [coverImageUrl];
  if (article.publishedAt) {
    schema.datePublished = new Date(article.publishedAt).toISOString();
  }
  if (article.updatedAt) {
    schema.dateModified = new Date(article.updatedAt).toISOString();
  }
  if (article.topic) {
    schema.articleSection = article.topic;
  }
  return schema;
}

/**
 * CollectionPage schema for "list of properties on a topic" pages like
 * /districts/[code]. Google's spec: Place is for a single physical
 * location, CollectionPage is right when the page lists multiple items
 * grouped by a topic. The list of properties goes in `mainEntity` as
 * an ItemList so each property links back to its detail page.
 */
export function collectionPageSchema(args: {
  name: string;
  description: string;
  url: string;
  items: Array<{ name: string; url: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: args.name,
    description: args.description,
    url: args.url,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: args.items.length,
      itemListElement: args.items.map((it, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: it.url,
        name: it.name,
      })),
    },
  };
}

/**
 * FAQPage schema from a list of Q&A pairs. Strips HTML from answers
 * because they're now authored as Quill HTML in the admin — Google's
 * FAQPage spec accepts HTML, but plain text reads cleaner in the
 * Rich Results snippet and AI Overview citations.
 */
export function faqPageSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: removeHTMLTags(f.answer || ""),
      },
    })),
  };
}

/**
 * The named agent behind the site. Pages that lean on Winnie's licence for
 * credibility (valuation, about) emit this so the person, the CEA
 * registration and the agency travel together. Details come from lib/seo's
 * AGENT constant, which is the single source of truth.
 */
export function agentPersonSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: AGENT.name,
    jobTitle: AGENT.jobTitle,
    identifier: `CEA ${AGENT.ceaRegNo}`,
    worksFor: {
      "@type": "Organization",
      name: AGENT.estateAgent,
    },
    url: `${SITE_URL}/about`,
  };
}

/**
 * WebApplication for the free calculators and tools (mortgage calculator,
 * compare tool). Tells Google the page is a working tool rather than an
 * article, and that using it costs nothing.
 */
export function webApplicationSchema(args: {
  name: string;
  url: string;
  description: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: args.name,
    url: args.url,
    description: args.description,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "SGD",
    },
  };
}

interface PropertyForSchema {
  name: string;
  title?: string;
  description?: string;
  full_address?: string;
  address?: string;
  city?: string;
  pin_code?: string;
  district?: string;
  project_developer?: string;
  construction_status?: string;
  isPreLaunch?: boolean;
  units?: number | null;
  min_flat_price?: number;
  max_flat_price?: number;
  is_leasehold?: boolean;
  lease_year?: number;
  updatedAt?: string | Date;
  createdAt?: string | Date;
  images?: { property_images?: string[] };
  youtube_videos?: string[];
  location?: { coordinates?: number[]; latitude?: string; longitude?: string };
  // flat_type_info keys are bedroom-type labels e.g. "2 Bedroom", "3 Bedroom"
  flat_type_info?: Record<string, number>;
}

/**
 * RealEstateListing schema for the single-property page. Uses dual
 * @type Product+Residence because Google rich results accept Product
 * (gets the price snippet) and Residence carries the geo signal.
 *
 * Required by Google for Product rich results: name, image, offers.
 * Everything else strengthens the snippet but isn't strictly required.
 */
export function propertySchema(p: PropertyForSchema, canonicalUrl: string) {
  const imageBase = process.env.NEXT_PUBLIC_IMAGE_URL || "";
  const images =
    p.images?.property_images && imageBase
      ? p.images.property_images
          .slice(0, 5)
          .map(
            (img) =>
              `${imageBase}/${formatProjectName(p.name)}/property_images/${img}`,
          )
      : undefined;

  // Fix 1 — description fallback.
  // Some properties have empty or whitespace-only descriptions scraped
  // from the developer site. Strip HTML first, then fall back to a
  // deterministic sentence so the schema never ships without a description.
  const rawDesc = removeHTMLTags(p.description || "").trim();
  const tenureLabel =
    p.is_leasehold === false
      ? "freehold"
      : p.lease_year
        ? `${p.lease_year}-year leasehold`
        : "leasehold";
  const fallbackDesc = `${p.title || p.name} is a ${tenureLabel} new launch condominium in Singapore${p.district ? `, District ${p.district.replace(/^D/, "")}` : ""}. Compare floor plans, pricing, and availability on AI Property.`;
  const description = (rawDesc.length > 20 ? rawDesc : fallbackDesc).slice(0, 500);

  // Fix 2 — numberOfRooms from flat_type_info.
  // flat_type_info keys are bedroom labels like "2 Bedroom", "3 Bedroom".
  // Extract the numeric bedroom counts and report min–max range.
  const bedroomCounts: number[] = Object.keys(p.flat_type_info ?? {})
    .map((k) => {
      const m = k.match(/(\d+)\s*bed/i);
      return m ? parseInt(m[1], 10) : NaN;
    })
    .filter(Number.isFinite) as number[];
  const minBeds = bedroomCounts.length ? Math.min(...bedroomCounts) : null;
  const maxBeds = bedroomCounts.length ? Math.max(...bedroomCounts) : null;

  const [longitude, latitude] = p.location?.coordinates ?? [];

  /**
   * A project with no released price is not a Product: there is nothing to
   * offer, no price and no availability. Pre-launch pages were still
   * emitting Product with an Offer built from whatever min_flat_price the
   * CMS happened to hold, which asserted prices that do not exist.
   *
   * So: ApartmentComplex while a project is pre-priced, and the existing
   * Product + Residence once there is a real price to quote. That keeps
   * price rich results on the projects that legitimately have one.
   */
  const priceFrom =
    p.min_flat_price && p.min_flat_price >= 10_000 ? p.min_flat_price : null;
  const hasRealPrice = priceFrom !== null;
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": hasRealPrice ? ["Product", "Residence"] : "ApartmentComplex",
    name: p.title || p.name,
    url: canonicalUrl,
    description,
    // Fix 3 — tourBookingPage: points to the enquiry form on this same
    // page. Signals to Google and AI engines that a viewing can be booked
    // directly — strengthens the listing's rich result eligibility.
    tourBookingPage: `${canonicalUrl}#enquire`,
    ...(images && images.length > 0 ? { image: images } : {}),
    ...(p.project_developer
      ? { brand: { "@type": "Brand", name: p.project_developer } }
      : {}),
    ...(p.updatedAt ? { dateModified: new Date(p.updatedAt).toISOString() } : {}),
    // Fix 2 cont. — add numberOfRooms only when bedroom data is available.
    ...(minBeds !== null
      ? {
          numberOfRooms: minBeds === maxBeds ? minBeds : `${minBeds}–${maxBeds}`,
        }
      : {}),
  };

  // Address — combine what's available. PostalAddress.addressCountry
  // is required for Google to use the geo data.
  const addressLine = p.full_address || p.address;
  if (addressLine) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: addressLine,
      ...(p.city ? { addressLocality: p.city } : {}),
      ...(p.pin_code ? { postalCode: p.pin_code } : {}),
      addressCountry: "SG",
    };
  }

  // Geo coordinates if present.
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude,
      longitude,
    };
  }

  // Unit count, which ApartmentComplex takes directly. Pre-launch counts
  // are tender estimates, so say so rather than presenting a firm number.
  if (!hasRealPrice && p.units && p.units > 0) {
    schema.numberOfAccommodationUnits = {
      "@type": "QuantitativeValue",
      value: p.units,
      ...(p.isPreLaunch ? { description: "Estimate, not yet confirmed by the developer" } : {}),
    };
  }

  // Offer block. Google needs at least one Offer with price+currency
  // to surface the price rich snippet. Floor at SGD 10,000 — any real
  // SG property clears that comfortably, so anything below is a
  // scraper/admin placeholder we shouldn't expose to crawlers.
  if (hasRealPrice) {
    const availability =
      p.construction_status === "Sold Out"
        ? "https://schema.org/SoldOut"
        : p.construction_status === "Move In"
          ? "https://schema.org/InStock"
          : "https://schema.org/PreOrder";
    schema.offers = {
      "@type": "Offer",
      price: priceFrom,
      priceCurrency: "SGD",
      url: canonicalUrl,
      availability,
      ...(p.max_flat_price && p.max_flat_price > priceFrom
        ? {
            priceSpecification: {
              "@type": "PriceSpecification",
              minPrice: priceFrom,
              maxPrice: p.max_flat_price,
              priceCurrency: "SGD",
            },
          }
        : {}),
    };
  }

  // VideoObject entries for the project's YouTube videos, when any link is valid.
  const videoIds = (p.youtube_videos ?? []).map(youtubeId).filter((id): id is string => Boolean(id));
  if (videoIds.length > 0) {
    schema.video = videoIds.map((id, i) => ({
      "@type": "VideoObject",
      name: `${p.title || p.name} video ${i + 1}`,
      thumbnailUrl: youtubeThumb(id),
      embedUrl: youtubeEmbedUrl(id),
      contentUrl: youtubeWatchUrl(id),
      description: removeHTMLTags(p.description || "").slice(0, 200) || `${p.title || p.name} in Singapore`,
      // createdAt is stable; updatedAt moves on every unrelated save.
      ...(p.createdAt || p.updatedAt ? { uploadDate: new Date(p.createdAt || p.updatedAt!).toISOString() } : {}),
    }));
  }

  return schema;
}
